// src/utils/cityCountryUtils.js

import countryData from './countries.json';

// Create a map of cities to country codes
const cityToCountryMap = {};

// Build city lookup from states data
countryData.forEach(country => {
  const countryCode = country.iso2; // e.g., "AX", "AL"
  
  // Add capital city
  if (country.capital) {
    const capitalKey = country.capital.toLowerCase().trim();
    cityToCountryMap[capitalKey] = countryCode;
  }
  
  // Add all cities from states
  if (country.states && Array.isArray(country.states)) {
    country.states.forEach(state => {
      if (state.name) {
        const cityKey = state.name.toLowerCase().trim();
        cityToCountryMap[cityKey] = countryCode;
        
        // Also check for state_code as potential city name
        if (state.state_code) {
          const stateCodeKey = state.state_code.toLowerCase().trim();
          cityToCountryMap[stateCodeKey] = countryCode;
        }
      }
    });
  }
});

// console.log(`Loaded ${Object.keys(cityToCountryMap).length} cities from countries data`);

// Helper function to get country code for a city
const getCountryCodeForCity = (cityName) => {
  if (!cityName) return null;
  
  const normalizedCity = cityName.toLowerCase().trim();
  
  // Direct lookup
  if (cityToCountryMap[normalizedCity]) {
    return cityToCountryMap[normalizedCity];
  }
  
  // Fuzzy search for partial matches
  const cityKeys = Object.keys(cityToCountryMap);
  for (const cityKey of cityKeys) {
    if (cityKey.includes(normalizedCity) || normalizedCity.includes(cityKey)) {
      return cityToCountryMap[cityKey];
    }
  }
  
  return null;
};

// Get country details from country code
const getCountryByCode = (countryCode) => {
  if (!countryCode) return null;
  return countryData.find(country => 
    country.iso2 === countryCode.toUpperCase() || 
    country.iso3 === countryCode.toUpperCase()
  );
};

// Get country name from code
const getCountryName = (countryCode) => {
  const country = getCountryByCode(countryCode);
  return country ? country.name : countryCode;
};

// Search cities in a country
const getCitiesInCountry = (countryCode) => {
  const country = getCountryByCode(countryCode);
  if (!country) return [];
  
  const cities = [];
  
  // Add capital
  if (country.capital) {
    cities.push({
      name: country.capital,
      type: 'capital',
      latitude: country.latitude,
      longitude: country.longitude
    });
  }
  
  // Add states/cities
  if (country.states && Array.isArray(country.states)) {
    country.states.forEach(state => {
      if (state.name) {
        cities.push({
          name: state.name,
          type: 'state/city',
          latitude: state.latitude,
          longitude: state.longitude,
          stateCode: state.state_code
        });
      }
    });
  }
  
  return cities;
};

// Check if a city exists in our database
const cityExists = (cityName) => {
  return getCountryCodeForCity(cityName) !== null;
};

// Get all country codes
const getAllCountryCodes = () => {
  return countryData.map(country => ({
    code: country.iso2,
    name: country.name,
    capital: country.capital
  }));
};

export {
  getCountryCodeForCity,
  getCountryByCode,
  getCountryName,
  getCitiesInCountry,
  cityExists,
  getAllCountryCodes,
  cityToCountryMap
};

export default getCountryCodeForCity;